import React from 'react';

import classNames from 'classnames';
import CrossIcon from 'icons/ic-close.svg';
import TickIcon from 'icons/ic-tick.svg';
import ProgressIcon from 'icons/progress.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';

export default function ReportGeneratorModal({
  isOpen,
  onCancelClick,
  onDownloadReadyClick,
  onTryAgainClick,
  progress,
  type,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.components.ReportGeneratorModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onCancelClick}
            size='md'
          >
            {i18nTShared('cancel')}
          </TextButton>
          {!(progress === 'noResults') && (
            <FilledButton
              color='mint'
              disabled={['initial', 'generating'].includes(progress)}
              onClick={() => {
                if (progress === 'downloadReady') {
                  onDownloadReadyClick();
                } else if (progress === 'failed') {
                  onTryAgainClick();
                }
              }}
            >
              <ProgressIcon
                className={classNames(
                  progress === 'generating' ? 'tw-mr-1' : 'hide',
                )}
                height={18}
                width={18}
              />
              {progress === 'downloadReady' ?
                i18nT('button.download')
              : progress === 'failed' ?
                i18nTShared('tryAgain')
              : progress === 'generating' && i18nTShared('pleaseWait...')}
            </FilledButton>
          )}
        </div>
      }
      isOpen={isOpen}
      mode='flexible'
      onClose={onCancelClick}
    >
      <div className='tw-p-8'>
        <h2 className='tw-m-0 tw-mb-6 tw-p-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
          <div className='tw-flex tw-h-7 tw-items-center'>
            {progress === 'generating' ?
              i18nT(`heading.generating.${type}`)
            : progress === 'downloadReady' ?
              <>
                <div className='tw-mr-3 tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-green-500'>
                  <TickIcon
                    className='[&_polygon]:tw-fill-white'
                    height={20}
                    width={20}
                  />
                </div>
                <div>{i18nT('heading.downloadReady')}</div>
              </>
            : ['failed', 'noResults'].includes(progress) && (
                <>
                  <div className='tw-mr-3 tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-600'>
                    <CrossIcon
                      className='[&_polygon]:tw-fill-white'
                      height={24}
                      width={24}
                    />
                  </div>
                  <div>{i18nT(`heading.${progress}`)}</div>
                </>
              )
            }
          </div>
        </h2>
        <p className='tw-m-0 tw-p-0'>
          {['downloadReady', 'failed', 'generating', 'noResults'].includes(
            progress,
          ) && i18nT(`description.${progress}`)}
        </p>
      </div>
    </ModalBox>
  );
}

ReportGeneratorModal.propTypes = {
  type: PropTypes.oneOf([
    'completedTraining',
    'currentTrainingStatus',
    'expiringTraining',
    'trainingMatrix',
  ]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  progress: PropTypes.oneOf([
    'initial',
    'generating',
    'downloadReady',
    'noResults',
    'failed',
  ]).isRequired,
  onCancelClick: PropTypes.func.isRequired,
  onDownloadReadyClick: PropTypes.func.isRequired,
  onTryAgainClick: PropTypes.func.isRequired,
};
